import React from 'react';
import { CheckIcon, InfoIcon, TagIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { hasPhoto } from '../../data/assets';
import { isWeekend, offerMayApply } from '../../data/offers';
import { usd } from '../../utils/format';
import type { TruckPref } from '../../types/funnel';
import { TruckIllustration } from './TruckIllustration';
import { BrandPhoto } from './BrandPhoto';
const copy = {
  en: {
    truckTitle: 'Which truck would you like?',
    truckSub: 'Pick a size or let us choose. This is your preference. Dispatch assigns the actual truck based on your list, access and fleet availability.',
    help: 'Help me choose',
    helpBody: 'We match the truck to your list.',
    likely: 'Likely fit for your list',
    illus: 'Illustrations drawn to scale between sizes, not photos of a specific truck.',
    small: 'Your list may not fit this size in one trip. Our team will review and suggest an adjustment before anything is confirmed.',
    sizes: {
      '16': 'Small apartment loads',
      '20': 'Most 1–2 bedroom homes',
      '26': 'Larger homes'
    },
    crewTitle: 'How many movers?',
    crewSub: 'Every crew comes with 1 moving truck. We recommend one option based on your items and access.',
    recommended: 'Recommended',
    m2: '2 movers + 1 truck',
    m3: '3 movers + 1 truck',
    weekday: 'Reduced weekday rates are available for simple, packed moves with easy access. Our team confirms eligibility.',
    weekend: 'Weekend rate for 2 movers.',
    lower: 'You chose fewer movers than recommended. We’ll confirm dispatch can complete it safely; it may take longer.'
  },
  es: {
    truckTitle: '¿Qué camión prefiere?',
    truckSub: 'Elija un tamaño o deje que lo elijamos. Es su preferencia: despacho asigna el camión real según su lista, el acceso y la flota disponible.',
    help: 'Ayúdenme a elegir',
    helpBody: 'Asignamos el camión según su lista.',
    likely: 'Probablemente adecuado para su lista',
    illus: 'Ilustraciones a escala entre tamaños; no son fotos de un camión específico.',
    small: 'Es posible que su lista no quepa en un viaje con este tamaño. Nuestro equipo lo revisará y le propondrá un ajuste antes de confirmar.',
    sizes: {
      '16': 'Cargas de apartamento pequeño',
      '20': 'La mayoría de hogares de 1–2 recámaras',
      '26': 'Hogares más grandes'
    },
    crewTitle: '¿Cuántos trabajadores?',
    crewSub: 'Cada equipo incluye 1 camión de mudanza. Le recomendamos una opción según sus artículos y el acceso.',
    recommended: 'Recomendado',
    m2: '2 trabajadores + 1 camión',
    m3: '3 trabajadores + 1 camión',
    weekday: 'Hay tarifas reducidas entre semana para mudanzas sencillas, ya empacadas y con acceso fácil. Nuestro equipo confirma si aplica.',
    weekend: 'Tarifa de fin de semana para 2 trabajadores.',
    lower: 'Eligió menos trabajadores de lo recomendado. Confirmaremos que despacho pueda completarlo de forma segura; puede tomar más tiempo.'
  }
};
const truckOptions: {
  value: TruckPref;
  ft: 16 | 20 | 26 | null;
}[] = [{
  value: '16',
  ft: 16
}, {
  value: '20',
  ft: 20
}, {
  value: '26',
  ft: 26
}, {
  value: 'help',
  ft: null
}];
export function TruckCrewPicker() {
  const {
    lang,
    data,
    update,
    quote,
    config,
    track
  } = useFunnel();
  const c = copy[lang];
  const crew = quote.crew;
  const tooSmall = data.truckPreference && data.truckPreference !== 'help' && Number(data.truckPreference) < Number(quote.truckSuggestion);
  const weekend = isWeekend(data.moveDate);
  const showOffer = offerMayApply(data, crew.used) && !weekend;
  const chooseTruck = (v: TruckPref) => {
    update({
      truckPreference: v
    });
    track('TruckPreferenceSelected', {
      truck: v
    });
  };
  return <div className="space-y-10" data-id="element-1012" data-mp="1ryncf3cjj53b">
      <fieldset data-id="element-1013" data-mp="1rxc7v1b9ttb5">
        <legend className="font-display text-2xl font-black text-magenta" data-id="element-1014" data-mp="1rvrcj9h4u6c8">{c.truckTitle}</legend>
        <p className="mb-4 mt-1 max-w-2xl text-sm text-ink-600" data-id="element-1015" data-mp="2pfcaclyddn7f">{c.truckSub}</p>
        <div role="radiogroup" aria-label={c.truckTitle} className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1016" data-mp="2kkycmcw8j3zl">
          {truckOptions.map(o => {
          const sel = data.truckPreference === o.value;
          const likely = o.value === quote.truckSuggestion;
          return <button key={o.value} type="button" role="radio" aria-checked={sel} onClick={() => chooseTruck(o.value)} className={`relative flex flex-col overflow-hidden rounded-xl border-2 bg-white text-left transition-[border-color,transform] duration-150 ease-out active:scale-[0.98] ${sel ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-1017" data-mp="1v7n7eg60k2zy">
                <TruckIllustration lengthFt={o.ft} className="aspect-[32/15] w-full" data-id="element-1018" data-mp="2j2g4av07pp83" />
                {sel && <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-lime text-ink" aria-hidden="true" data-id="element-1019" data-mp="36s233hcoa2fk">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-1020" data-mp="104l1kq35t81u" />
                  </span>}
                <span className="flex flex-1 flex-col p-3" data-id="element-1021" data-mp="v5qg1gdt55e1">
                  <span className="font-display text-lg font-extrabold text-ink" data-id="element-1022" data-mp="18yg5xvwgiugf">{o.ft ? `${o.ft} ft` : c.help}</span>
                  <span className="text-xs text-ink-500" data-id="element-1023" data-mp="14w33phgx5eoe">{o.ft ? c.sizes[o.value as '16' | '20' | '26'] : c.helpBody}</span>
                  {likely && <span className="mt-2 self-start rounded bg-lime-soft px-1.5 py-0.5 text-[11px] font-bold text-ink" data-id="element-1024" data-mp="h70kw6ajtfsb">{c.likely}</span>}
                </span>
              </button>;
        })}
        </div>
        <p className="mt-2 text-xs text-ink-500" data-id="element-1025" data-mp="q52j6e2wdubw">{c.illus}</p>
        {tooSmall && <p role="status" className="mt-3 flex items-start gap-2 rounded-lg bg-magenta-soft p-3 text-sm text-ink" data-id="element-1026" data-mp="lagfyy6yz82w">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-magenta-dark" aria-hidden="true" data-id="element-1027" data-mp="1wnixbvgw81o4" />
            {c.small}
          </p>}
      </fieldset>

      <fieldset data-id="element-1028" data-mp="u9bcs3y8yzry">
        <legend className="font-display text-2xl font-black text-magenta" data-id="element-1029" data-mp="u9tc59g7hu43">{c.crewTitle}</legend>
        <p className="mb-4 mt-1 max-w-2xl text-sm text-ink-600" data-id="element-1030" data-mp="z7cuakg0yr1b">{c.crewSub}</p>
        <div role="radiogroup" aria-label={c.crewTitle} className="grid gap-4 sm:grid-cols-2" data-id="element-1031" data-mp="13c2n20fdzpi0">
          {([2, 3] as const).map(n => {
          const sel = crew.used === n;
          const rec = crew.recommended === n;
          return <button key={n} type="button" role="radio" aria-checked={sel} onClick={() => update({
            crewPreference: n === crew.recommended ? null : n
          })} className={`relative flex flex-col overflow-hidden rounded-2xl border-2 bg-white text-left transition-[border-color,transform] duration-150 ease-out active:scale-[0.98] ${sel ? 'border-lime-dark ring-2 ring-lime' : 'border-ink-200 hover:border-ink-400'}`} data-id="element-1032" data-mp="fl6s4ufmwe6j">
                <BrandPhoto role={n === 3 && hasPhoto('crew') ? 'crew' : 'truck_choice'} alt="" className="aspect-[16/9] w-full" data-id="element-1033" data-mp="3lg293qrr81gr" />
                {rec && <span className="absolute left-3 top-3 rounded-md bg-lime px-2 py-1 text-xs font-bold text-ink" data-id="element-1034" data-mp="3biedkymbqpxj">{c.recommended}</span>}
                {sel && <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-lime text-ink" aria-hidden="true" data-id="element-1035" data-mp="37gjbhrwkk8ip">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-1036" data-mp="2xqhkar8x69zk" />
                  </span>}
                <span className="flex flex-1 items-baseline justify-between gap-3 p-4" data-id="element-1037" data-mp="2sr524jsztr3b">
                  <span className="font-display text-lg font-extrabold text-ink" data-id="element-1038" data-mp="2ntbudmc1kp0x">{n === 2 ? c.m2 : c.m3}</span>
                  <span className="whitespace-nowrap" data-id="element-1039" data-mp="2jrhydrcsk973">
                    <span className="font-display text-2xl font-black text-ink" data-id="element-1040" data-mp="2jt1cpcew0a68">{usd(config.rates[n], lang)}</span>
                    <span className="text-sm text-ink-500" data-id="element-1041" data-mp="6jp2dcfpnhyb">/h</span>
                  </span>
                </span>
                {n === 2 && (showOffer || weekend) && <span className="mx-4 mb-4 flex items-start gap-1.5 rounded-lg bg-lime-soft p-2 text-xs font-medium text-ink" data-id="element-1042" data-mp="bgfsksl8btxg">
                    <TagIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime-dark" aria-hidden="true" data-id="element-1043" data-mp="1k2qp7fj69rm" />
                    {weekend ? c.weekend : c.weekday}
                  </span>}
              </button>;
        })}
        </div>
        {data.crewPreference !== null && data.crewPreference < crew.recommended && <p className="mt-3 text-sm text-ink-600" data-id="element-1044" data-mp="3a1h0z9ocr05h">{c.lower}</p>}
      </fieldset>
    </div>;
}